<template>
    <div class="hts-form-checkbox">
        <fieldset class="hts-form-checkbox__container block form-control my-2">
            <span
                v-if="label"
                class="label">
                <span class="label-text">
                    {{ label }}<span
                        v-if="required"
                        class="text-error-500 ml-1">
                        *
                    </span>
                </span>
            </span>
            <div
                v-if="checkAll"
                class="my-2">
                <input
                    :id="`${name}-all`"
                    v-model="checkedAll"
                    type="checkbox"
                    class="checkbox inline-block align-middle mr-2"
                    :class="cColorSize"
                    :disabled="locked"
                    :indeterminate="indeterminate"
                    @click="toggleCheckAll" >
                <span class="inline-block align-middle">
                    {{ $t('labels.checkAll') }}
                </span>
            </div>

            <div
                class="flex gap-1 flex-wrap"
                :class="{ 'flex-col': type === 'list' }">
                <div
                    v-for="(item, index) in items"
                    :key="index"
                    class="mx-2 align-middle">
                    <input
                        :id="name"
                        type="checkbox"
                        :checked="getItemValue(item)"
                        :required="index === 0 && cRequired"
                        :disabled="locked"
                        class="checkbox inline-block align-middle mr-2"
                        :class="cColorSize"
                        data-type="check-box"
                        @change="onChange(item, $event)" >
                    <span
                        class="inline-block align-middle cursor-pointer"
                        @click="handleClickLabel(item, $event)">
                        {{ item[checkLabel] }}
                    </span>
                </div>
            </div>
        </fieldset>
    </div>
</template>

<script lang="ts" setup>
import { daisyuiColorSize } from '~/components/utils/daisyui'

const props = defineProps({
  modelValue: {
    type: [Array, Boolean],
    default: () => []
  },
  items: {
    type: Array as PropType<any[]>,
    required: true
  },
  type: String,
  prop: String,
  name: String,
  rules: Object,
  label: String,
  checkKey: {
    type: String,
    default: 'value'
  },
  checkLabel: {
    type: String,
    default: 'label'
  },
  checkAll: Boolean,
  required: Boolean,
  disabled: Boolean,
  readonly: Boolean,
  color: String,
  size: String
})
const { required, disabled, readonly } = toRefs(props)
const emits = defineEmits(['update:modelValue', 'change'])
const cColorSize = daisyuiColorSize('checkbox', props)
const { modelValue, items, checkKey, checkLabel } = toRefs(props)

const locked = computed(() => disabled.value || readonly.value)

const cRequired = computed<boolean>(() => {
  if (cValue.value !== undefined && cValue.value !== null) {
    if (Array.isArray(cValue.value)) {
      if (cValue.value.length) {
        return false
      } else {
        return required.value
      }
    }
    return false
  }
  return required.value
})
const itemsLength = computed<number>(() => {
  if (Array.isArray(items.value)) {
    return items.value.length
  }
  return 0
})
const indeterminate = computed<boolean>(() => {
  if (Array.isArray(cValue.value)) {
    return cValue.value.length > 0 && cValue.value.length < itemsLength.value
  }
  return false
})
const checkedAll = ref<boolean>(false)
onBeforeMount(() => {})
const cValue = computed({
  get () {
    if (Array.isArray(modelValue.value)) {
      if (modelValue.value.length >= itemsLength.value) {
        if (!modelValue.value.length) {
          checkedAll.value = false
        } else {
          checkedAll.value = true
        }
      }
    }
    return modelValue.value
  },
  set (val) {
    if (typeof modelValue.value === 'boolean') {
      emits('update:modelValue', !!val)
      emits('change', !!val)
    } else {
      emits('update:modelValue', val)
      emits('change', val)
    }
  }
})

const toggleCheckAll = () => {
  if (checkedAll.value) {
    cValue.value = []
  } else {
    cValue.value = items.value.map((i: any) => i[checkKey.value])
  }
}

watch(
  () => modelValue.value,
  (val) => {
    updateCheckedAll()
  }
)

const updateCheckedAll = () => {
  if (Array.isArray(cValue.value)) {
    if (cValue.value.length === items.value.length) {
      checkedAll.value = true
    } else {
      checkedAll.value = false
    }
  }
}

const onChange = (item: { [key: string]: any }, e: Event) => {
  if (locked.value) {
    return
  }
  const target = e.target as HTMLInputElement

  if (Array.isArray(cValue.value)) {
    const exists = cValue.value.findIndex((c: any) => c === item[checkKey.value])

    if (target.checked) {
      if (exists === -1) {
        cValue.value.push(item[checkKey.value])
      }
    } else if (exists !== -1) {
      cValue.value.splice(exists, 1)
    }
    updateCheckedAll()
    emits('change', cValue.value)
  }
  if (typeof cValue.value === 'boolean') {
    cValue.value = target.checked
    emits('change', target.checked)
  }
}

const getItemValue = (item: any): boolean => {
  if (typeof cValue.value === 'boolean') {
    return cValue.value
  }
  if (!Array.isArray(cValue.value)) {
    return false
  }
  return cValue.value.includes(item[checkKey.value])
}

const handleClickLabel = (item: any, e: any) => {
  if (locked.value) {
    return
  }
  if (e instanceof Event) {
    e.preventDefault()
    e.stopPropagation()
  }
  if (Array.isArray(cValue.value)) {
    const exists = cValue.value.findIndex((c) => c === item[checkKey.value])
    if (exists >= 0) {
      cValue.value.splice(exists, 1)
    } else {
      cValue.value.push(item[checkKey.value])
    }
    updateCheckedAll()
    emits('change', cValue.value)
  }
  if (typeof cValue.value === 'boolean') {
    cValue.value = !cValue.value
    nextTick(() => {
      emits('change', cValue.value)
    })
  }
}
</script>
